import { useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router';
import {
  LuBackpack,
  LuBuilding2,
  LuGraduationCap,
  LuLaptop,
  LuPresentation,
  LuSparkles,
  LuStore,
} from 'react-icons/lu';

import {
  CUSTOMER_INTEREST_SUGGESTIONS,
  type SelfServiceCategoryKey,
} from '@mds/shared/types/customer';
import { ChoiceList } from '@mds/shared/ui/form/choice-list';
import { ChoiceTiles, type ChoiceTile } from '@mds/shared/ui/form/choice-tiles';
import { Field } from '@mds/shared/ui/form/field';
import { Form, applyServerErrors, useZodForm } from '@mds/shared/ui/form/form';
import { FormSection } from '@mds/shared/ui/form/form-section';
import { PhoneInput } from '@mds/shared/ui/form/phone-input';
import { Button } from '@mds/shared/ui/shadcn/button';
import { Input } from '@mds/shared/ui/shadcn/input';

import PageHeader from '@/components/heading/page-header';
import OutcomePanel from '@/components/section/outcome-panel';
import { ErrorBlock } from '@/components/states';
import { signUp } from '@/features/customers/sign-up.api';
import { signUpDefaults, signUpSchema } from '@/features/customers/sign-up.schema';

const band = 'mx-auto max-w-page px-gutter pt-section';
/**
 * The form's column, centred on the page with its header. `box-content` keeps
 * the gutter outside the cap, so the column is the same width at every size.
 */
const column = 'mx-auto box-content max-w-3xl px-gutter pt-section';

/** The CRM's categories in the visitor's words. `member` is staff's to grant. */
const CATEGORY_TILES: (ChoiceTile & { value: SelfServiceCategoryKey })[] = [
  { value: 'student', label: 'Student', description: 'Studying right now', icon: LuBackpack },
  {
    value: 'graduate',
    label: 'Graduate',
    description: 'Finished studying recently',
    icon: LuGraduationCap,
  },
  { value: 'freelancer', label: 'Freelancer', description: 'Working for myself', icon: LuLaptop },
  {
    value: 'teacher',
    label: 'Teacher or trainer',
    description: 'I teach, coach or train',
    icon: LuPresentation,
  },
  { value: 'business', label: 'Business owner', description: 'I run a business', icon: LuStore },
  {
    value: 'company',
    label: 'Company or team',
    description: 'Here on behalf of an organisation',
    icon: LuBuilding2,
  },
  {
    value: 'visitor',
    label: 'Just curious',
    description: 'None of these quite fit',
    icon: LuSparkles,
  },
];

const INTEREST_OPTIONS = CUSTOMER_INTEREST_SUGGESTIONS.map((label) => ({ value: label, label }));

const CHANNEL_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'sms', label: 'SMS' },
];

/**
 * The sign-up page behind a shared link — on a poster, in a WhatsApp message,
 * at an event. Nothing on the site links here.
 *
 * What it saves is a CRM record, the same one staff add on the Customers page
 * in the admin app, less what only staff set: tags and internal notes.
 */
export default function JoinPage() {
  const form = useZodForm(signUpSchema, { defaultValues: signUpDefaults });
  /** A failure no field can show. `null` once dismissed or a new send starts. */
  const [formError, setFormError] = useState<unknown>(null);

  const sending = useMutation({
    mutationFn: signUp,
    onMutate: () => setFormError(null),
    onError: (error) => {
      if (!applyServerErrors(error, form.setError)) setFormError(error);
    },
  });

  if (sending.isSuccess) {
    // In place of the form: one still sitting there invites sending it twice.
    return (
      <div className={band}>
        <OutcomePanel
          tone="success"
          title={`Welcome to MDS, ${sending.variables.name.split(' ')[0]}`}
          description="You're on our list. We'll be in touch about the spaces, events and programmes that match what you told us."
          actions={
            <>
              <Button asChild size="lg">
                <Link to="/events">See upcoming events</Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/spaces">Explore our spaces</Link>
              </Button>
            </>
          }
        />
      </div>
    );
  }

  return (
    <section className={column}>
      <PageHeader
        title="Join the MDS community"
        description="Tell us a little about yourself and what you're into, and we'll keep you posted on the spaces, events and programmes that fit."
      />

      <Form
        form={form}
        className="mt-block flex flex-col gap-card"
        onSubmit={(values) => sending.mutate(values)}
      >
        <FormSection title="About you">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field name="name" label="Full name" required className="sm:col-span-2">
              <Input autoComplete="name" />
            </Field>
            <Field name="email" label="Email" required>
              <Input type="email" autoComplete="email" />
            </Field>
            <Field name="phone" label="Phone / WhatsApp" hint="Optional.">
              <PhoneInput />
            </Field>
          </div>
          <Field name="categoryKey" label="Which describes you best?" required>
            <ChoiceTiles options={CATEGORY_TILES} className="max-sm:grid-cols-1" />
          </Field>
        </FormSection>

        <FormSection title="Your interests">
          <Field name="interests" label="Pick as many as you like">
            <ChoiceList multiple options={INTEREST_OPTIONS} className="grid sm:grid-cols-2" />
          </Field>
          <Field
            name="otherInterest"
            label="Something else?"
            hint="Optional. One interest in your own words."
          >
            <Input maxLength={64} />
          </Field>
        </FormSection>

        <FormSection title="Staying in touch">
          <Field
            name="channels"
            label="Happy to hear from us by"
            hint="Only what you tick. You can ask us to stop at any time."
          >
            <ChoiceList multiple options={CHANNEL_OPTIONS} className="grid sm:grid-cols-3" />
          </Field>
        </FormSection>

        {formError !== null && (
          <ErrorBlock
            title="We couldn't save your details"
            error={formError}
            onDismiss={() => setFormError(null)}
          />
        )}

        <div className="flex justify-center">
          <Button type="submit" size="lg" className="min-w-56" disabled={sending.isPending}>
            {sending.isPending ? 'Sending…' : 'Join'}
          </Button>
        </div>
      </Form>
    </section>
  );
}
